/**
 * The write layer for the money screens.
 *
 * Screens never touch Dexie and never touch the network. They call a
 * `MoneyActions` object, whose only real implementation forwards to
 * `mutations.*` — the one place a write reaches the outbox (docs/02 §8). Tests
 * pass a recording double instead, which is why the screens below can be
 * exercised without a database.
 */

import {
  type Bill,
  type BillInstance,
  type BillPayment,
  type ExpenseId,
  type ExpenseSource,
  type ExpenseSplit,
  type MemberId,
  type PaymentId,
  type PaymentMethod,
  type Piastres,
  type SplitKind,
  asId,
} from '@office/domain'
import { type SplitParticipant, expenseFromBillPayment } from '@office/domain/money'
import { mutations, type MutationContext } from '../../lib/mutations.js'

export interface NewExpense {
  description: string
  amount: Piastres
  category: string
  splitKind: SplitKind
  splits: readonly ExpenseSplit[]
  /** Provenance. `bill_payment` and `food_order` carry the record they came from. */
  source: ExpenseSource
  sourceRef: string | null
}

export interface NewBillPayment {
  bill: Bill
  instance: BillInstance
  amount: Piastres
  method: PaymentMethod
  /** Who the automatically-created shared expense is split between. */
  participants: readonly SplitParticipant[]
}

export interface NewSettlement {
  toMemberId: MemberId
  amount: Piastres
  method: PaymentMethod
}

export interface MoneyActions {
  addExpense(input: NewExpense): Promise<void>
  /**
   * Records the payment AND the shared expense it implies. A bill payment is a
   * shared expense (docs/01 §7.2) — "one ledger, not two" is a property of the
   * write, not a report generated later.
   */
  payBill(input: NewBillPayment): Promise<void>
  settle(input: NewSettlement): Promise<void>
}

/**
 * The expense id passed to the domain while composing a payload. The real id is
 * minted by the outbox (the mutation's own UUIDv7 becomes the row's primary
 * key), so this value is never stored and never leaves this module.
 */
const DRAFT_EXPENSE_ID = asId<ExpenseId>('00000000-0000-7000-8000-000000000000')

/**
 * Stands in for the payment id while the split is validated, before the outbox
 * has minted the real one. Like `DRAFT_EXPENSE_ID`, it never leaves this module.
 */
const PROBE_PAYMENT_ID = '00000000-0000-7000-8000-000000000001'

/**
 * `mutations.addExpense` types its payload without `source`/`sourceRef`, but the
 * server reads both (migrations/004: `COALESCE(d->>'source','manual')`,
 * `NULLIF(d->>'sourceRef','')::uuid`) and enforces `one_expense_per_source`.
 * Passing them through is what stops a replayed — or later server-generated —
 * bill-payment expense from being counted twice.
 */
interface ExpensePayload {
  description: string
  amount: number
  category: string
  splitKind: string
  splits: { personId: string; shareAmount: number; settledImmediately: boolean }[]
  source: string
  sourceRef: string
}

const expensePayload = (e: NewExpense): ExpensePayload => ({
  description: e.description,
  amount: e.amount,
  category: e.category,
  splitKind: e.splitKind,
  splits: e.splits.map(s => ({
    personId: s.personId,
    shareAmount: s.shareAmount,
    settledImmediately: s.settledImmediately,
  })),
  source: e.source,
  sourceRef: e.sourceRef ?? '',
})

export function actionsFromMutations(ctx: MutationContext): MoneyActions {
  return {
    async addExpense(input) {
      await mutations.addExpense(ctx, expensePayload(input))
    },

    async payBill({ bill, instance, amount, method, participants }) {
      const draftPayment = (id: string): BillPayment => ({
        id: asId<PaymentId>(id),
        billInstanceId: instance.id,
        amount,
        paidAt: ctx.now(),
        paidByMemberId: asId<MemberId>(ctx.actorId),
        method,
        note: null,
        receiptRef: null,
      })

      // Build the expense BEFORE anything reaches the outbox. "One ledger, not
      // two" (docs/01 §7.2) is a property of the write, so a split that cannot
      // be built — no participants, mismatched refs, exact shares that do not
      // sum — must fail with an empty queue. Enqueueing the payment first and
      // discovering the problem afterwards would leave a bill payment that
      // never became a shared expense, permanently, while the screen showed an
      // error implying nothing had happened at all. The id is a placeholder:
      // only the real one, minted by the outbox below, is ever stored.
      expenseFromBillPayment(bill, instance, draftPayment(PROBE_PAYMENT_ID), {
        expenseId: DRAFT_EXPENSE_ID,
        participants,
      })

      // The outbox mints the payment's id, and the server inserts the row under
      // that same id — so the expense can reference it before either exists.
      const queued = await mutations.payBill(ctx, instance.id, amount, method)
      const payment = draftPayment(queued.id)
      const expense = expenseFromBillPayment(bill, instance, payment, {
        expenseId: DRAFT_EXPENSE_ID,
        participants,
      })
      await mutations.addExpense(
        ctx,
        expensePayload({
          description: expense.description,
          amount: expense.amount,
          category: expense.category,
          splitKind: expense.splitKind,
          splits: expense.splits,
          source: 'bill_payment',
          sourceRef: payment.id,
        }),
      )
    },

    async settle({ toMemberId, amount, method }) {
      await mutations.settle(ctx, toMemberId, amount, method)
    },
  }
}
